import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';

// PRD PM §5.3 (Task 29) — çalışan dönem sonu sekmeli sonuç ekranı ("Dönem
// Sonuçlarım"). Gerçek uç (Task 14 — MyPeriodResultsController'dan grep ile
// doğrulanmış; brief metnindeki "GET v1/my/period-results?periodId" (query
// param) sapıyor, gerçekte PATH param — MyGoalsPage/MyEvaluatorsPage
// emsallerindeki "brief'in fiil/rota adı gerçek koddan sapabilir" dersiyle
// aynı):
//   GET v1/my/period-results/{periodId} → { goalsTab?, feedbackTab? }
//     (bkz. MyPeriodResultsDto — GoalsTab: List<PeriodGoalResponse>?,
//     FeedbackTab: EmployeeFeedbackSummaryDto?; hangi sekme null ise o sistem
//     dönemde pasif — FE o sekmeyi hiç göstermez)
// Erken erişimde (dönem henüz kapanmadı / kalibrasyon yönetici onayına
// ulaşmadı) 409 performance.results.notReady döner. Aynı çağrıda teorik
// olarak 404 performance.period.notFound (bilinmeyen dönem) veya 409
// module.notSubscribed (PM aboneliği kalkmış) da dönebilir — bu üç durumun
// HİÇBİRİ interceptor'ın genel hata toast'ını TETİKLEMEZ: `skipErrorToast:
// true` (my-offboarding/config'teki exit-interview çağrısıyla birebir aynı
// desen) hem toast'ı bastırır hem de GET zaten hata durumunda interceptor
// tarafından reddedilmek yerine EMPTY_RESPONSE ile çözülür (services/api.js).
// Üçü de aynı jenerik "sonuçlar hazır değil" boş-durumuna düşer — sayfanın
// hangi nedenle boş kaldığını ayırt etmesi gerekmiyor (bkz. task-29-report.md
// CONCERNS: bu bilinçli bir basitleştirme, üçü de kullanıcı için aynı sonucu
// doğuruyor: "şu an gösterilecek bir şey yok").
const fetchPeriodsByStatus = async (status) => {
  const res = await api.get('/v1/evaluation-periods', { params: { 'status[eq]': status, pageSize: 50 } });
  const r = res.data?.data;
  return Array.isArray(r) ? r : (r?.data || []);
};

// Sonuç ekranı yalnız KAPANMIŞ veya KAPANIŞ AŞAMASINDAKİ dönemler için anlamlı
// (period-goals/config'teki active+evaluation ikilisinin dönem-sonu eşleniği
// — EvaluationPeriodFilterRequest yalnız tek-değerli status[eq] destekliyor,
// bu yüzden iki ayrı GET atılıp client-side birleştirilir).
export const useResultsPeriods = () => {
  const [periods, setPeriods] = useState([]);
  const [periodsFetching, setPeriodsFetching] = useState(true);

  const fetchPeriods = useCallback(async () => {
    setPeriodsFetching(true);
    try {
      const [closed, pendingClose] = await Promise.all([
        fetchPeriodsByStatus('closed'),
        fetchPeriodsByStatus('pending_close'),
      ]);
      setPeriods([...closed, ...pendingClose]);
    } catch {
      setPeriods([]);
    } finally {
      setPeriodsFetching(false);
    }
  }, []);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  return { periods, periodsFetching };
};

// result: { goalsTab, feedbackTab, acknowledgement, objectionWindowEndsAt } | null
// P1-C genişlemesi: acknowledgement {acknowledgedAt, counterNote}|null +
// objectionWindowEndsAt (DateTime|null — ClosedAt + itiraz penceresi iş günü;
// null → pencere hesaplanamıyor, FE itiraz butonunu pasifler) + acknowledge()
// mutasyonu (POST .../acknowledge — idempotent, BE mevcut kaydı aynen döner).
// Plan 5 Task 2 genişlemesi: finalLevelLabel (string|null) — BE'nin tebliğ
// koşulu (period.DiscloseFinalLevel && period.Status=="closed" &&
// CalibrationRecord.FinalLevelId != null; bkz. Task 1 raporu) sağlandığında
// JSON'da gelir, aksi halde property JSON'a HİÇ yazılmaz ([JsonIgnore
// WhenWritingNull]) — `body.data?.finalLevelLabel` bu durumda undefined olur
// ve `|| null` ile normalize edilir. FE bu koşulu HESAPLAMAZ, yalnız alanın
// var/yok olmasına göre render eder (DTO-driven).
export const useMyPeriodResultsApi = (periodId) => {
  const [goalsTab, setGoalsTab] = useState(null);
  const [feedbackTab, setFeedbackTab] = useState(null);
  const [acknowledgement, setAcknowledgement] = useState(null);
  const [objectionWindowEndsAt, setObjectionWindowEndsAt] = useState(null);
  const [finalLevelLabel, setFinalLevelLabel] = useState(null);
  const [notReady, setNotReady] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!periodId) {
      setGoalsTab(null);
      setFeedbackTab(null);
      setAcknowledgement(null);
      setObjectionWindowEndsAt(null);
      setFinalLevelLabel(null);
      setNotReady(false);
      return undefined;
    }

    let cancelled = false;
    setFetching(true);
    api.get(`/v1/my/period-results/${periodId}`, { skipErrorToast: true })
      .then((res) => {
        if (cancelled) return;
        const body = res.data;
        if (body?.status) {
          setGoalsTab(body.data?.goalsTab || null);
          setFeedbackTab(body.data?.feedbackTab || null);
          setAcknowledgement(body.data?.acknowledgement || null);
          setObjectionWindowEndsAt(body.data?.objectionWindowEndsAt || null);
          setFinalLevelLabel(body.data?.finalLevelLabel || null);
          setNotReady(false);
        } else {
          setGoalsTab(null);
          setFeedbackTab(null);
          setAcknowledgement(null);
          setObjectionWindowEndsAt(null);
          setFinalLevelLabel(null);
          setNotReady(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setGoalsTab(null);
        setFeedbackTab(null);
        setAcknowledgement(null);
        setObjectionWindowEndsAt(null);
        setFinalLevelLabel(null);
        setNotReady(true);
      })
      .finally(() => { if (!cancelled) setFetching(false); });

    return () => { cancelled = true; };
  }, [periodId, refreshTrigger]);

  // "Okudum" onayı — hata durumunda interceptor toast'ı gösterir (mutasyon;
  // GET'lerdeki skipErrorToast bilinçli olarak YOK).
  const acknowledge = useCallback(async (counterNote) => {
    if (!periodId) return false;
    try {
      const res = await api.post(`/v1/my/period-results/${periodId}/acknowledge`, {
        counterNote: counterNote || null,
      });
      const dto = res.data?.data;
      if (dto) setAcknowledgement(dto);
      return true;
    } catch {
      return false;
    }
  }, [periodId]);

  return {
    goalsTab, feedbackTab, acknowledgement, objectionWindowEndsAt, finalLevelLabel,
    notReady, fetching, refresh, acknowledge,
  };
};

// P1-C — çalışan trend geçmişi ("Gelişim Grafiğim"). GET history yalnız KAPALI
// ve sonuçları görünür dönemleri döner ({periodId, name, closedAt,
// systemAScore?, weightedOverall?} — FinalLevel BE tarafından ASLA gönderilmez).
export const useMyResultsHistory = () => {
  const [history, setHistory] = useState([]);
  const [historyFetching, setHistoryFetching] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/v1/my/period-results/history', { skipErrorToast: true })
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.data;
        setHistory(Array.isArray(items) ? items : []);
      })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setHistoryFetching(false); });
    return () => { cancelled = true; };
  }, []);

  return { history, historyFetching };
};

// P1-C — sonuç itirazı: B paketinin (İtiraz Penceresi) plan'da SABİTLENMİŞ
// kontratı — POST v1/objections/my {periodId, note} + GET v1/objections/my
// ?periodId[eq]=. B henüz merge edilmediyse GET sessizce boş döner
// (skipErrorToast), POST hatası interceptor toast'ına düşer.
export const useMyObjection = (periodId) => {
  const [objection, setObjection] = useState(null);
  const [objectionFetching, setObjectionFetching] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (!periodId) {
      setObjection(null);
      return undefined;
    }
    let cancelled = false;
    setObjectionFetching(true);
    api.get('/v1/objections/my', { params: { 'periodId[eq]': periodId }, skipErrorToast: true })
      .then((res) => {
        if (cancelled) return;
        const r = res.data?.data;
        const list = Array.isArray(r) ? r : (r?.data || []);
        setObjection(list.length > 0 ? list[0] : null);
      })
      .catch(() => { if (!cancelled) setObjection(null); })
      .finally(() => { if (!cancelled) setObjectionFetching(false); });
    return () => { cancelled = true; };
  }, [periodId, refreshTrigger]);

  const submitObjection = useCallback(async (note) => {
    if (!periodId) return false;
    try {
      await api.post('/v1/objections/my', { periodId, note });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, [periodId]);

  return { objection, objectionFetching, submitObjection };
};
